<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { KeyRound, LoaderCircle, LockKeyhole, Phone, ShieldCheck, X } from 'lucide-vue-next'

import { useMonitorStore } from '@/stores/monitor'
import type { AuthorizationState } from '@/types/monitor'

const props = defineProps<{
  open: boolean
  authorization: AuthorizationState
  defaultPhone: string
}>()

const emit = defineEmits<{
  close: []
}>()

const store = useMonitorStore()
const { t } = useI18n()
const phone = ref(props.defaultPhone)
const code = ref('')
const password = ref('')
const passwordHint = ref<string | null>(null)
const pending = ref(false)
const error = ref<string | null>(null)

const stage = computed<'phone' | 'code' | 'password' | 'done'>(() => {
  if (props.authorization === 'authorized') return 'done'
  if (props.authorization === 'password_required') return 'password'
  if (props.authorization === 'code_requested') return 'code'
  return 'phone'
})

watch(
  () => props.open,
  (open) => {
    if (open) error.value = null
  },
)

async function submit(): Promise<void> {
  pending.value = true
  error.value = null
  try {
    if (stage.value === 'phone') {
      await store.requestLoginCode(phone.value)
    } else if (stage.value === 'code') {
      const response = await store.verifyLoginCode(code.value)
      passwordHint.value = response.hint
      code.value = ''
      if (response.authorization === 'authorized') emit('close')
    } else if (stage.value === 'password') {
      await store.verifyPassword(password.value)
      password.value = ''
      emit('close')
    } else {
      emit('close')
    }
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : String(caught)
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="open" class="modal-backdrop" @click.self="$emit('close')">
        <section class="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title">
          <button class="modal-close icon-button" type="button" :title="t('common.close')" @click="$emit('close')">
            <X :size="17" />
          </button>
          <div class="auth-visual">
            <div class="auth-rings"><span></span><span></span><ShieldCheck :size="34" /></div>
            <span>{{ t('telegramAuth.secureSession') }}</span>
          </div>
          <div class="auth-copy">
            <span class="modal-eyebrow">{{ t('telegramAuth.eyebrow') }}</span>
            <h2 id="auth-title">
              {{ stage === 'phone' ? t('telegramAuth.establish') : stage === 'code' ? t('telegramAuth.verifyCode') : stage === 'password' ? t('telegramAuth.twoStep') : t('telegramAuth.authorized') }}
            </h2>
            <p>
              {{ stage === 'phone'
                ? t('telegramAuth.phoneHelp')
                : stage === 'code'
                  ? t('telegramAuth.codeHelp')
                  : stage === 'password'
                    ? t('telegramAuth.passwordHelp', { hint: passwordHint ? t('telegramAuth.hint', { hint: passwordHint }) : '' })
                    : t('telegramAuth.authorizedHelp') }}
            </p>

            <form @submit.prevent="submit">
              <label v-if="stage === 'phone'" class="auth-field">
                <span>{{ t('telegramAuth.phone') }}</span>
                <div><Phone :size="16" /><input v-model.trim="phone" autocomplete="tel" placeholder="+1 202 555 0100" required /></div>
              </label>
              <label v-else-if="stage === 'code'" class="auth-field">
                <span>{{ t('telegramAuth.code') }}</span>
                <div><KeyRound :size="16" /><input v-model.trim="code" inputmode="numeric" autocomplete="one-time-code" placeholder="12345" required /></div>
              </label>
              <label v-else-if="stage === 'password'" class="auth-field">
                <span>{{ t('telegramAuth.password') }}</span>
                <div><LockKeyhole :size="16" /><input v-model="password" type="password" autocomplete="current-password" required /></div>
              </label>

              <p v-if="error" class="form-error">{{ error }}</p>
              <button class="button button--primary auth-submit" type="submit" :disabled="pending">
                <LoaderCircle v-if="pending" class="spin" :size="16" />
                <ShieldCheck v-else :size="16" />
                {{ pending ? t('telegramAuth.contacting') : stage === 'phone' ? t('telegramAuth.sendCode') : stage === 'code' ? t('telegramAuth.verify') : stage === 'password' ? t('telegramAuth.unlock') : t('telegramAuth.continue') }}
              </button>
            </form>
            <small>{{ t('telegramAuth.footnote') }}</small>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

